import React, { useState } from "react";
import type { EhrPatientContext } from "./elationPatientToContext";
import {
  ehrSourceSystemLabel,
  type EhrMemberMatch,
} from "../graphql/resolveEhrMember";
import type { ResolutionState } from "./resolutionState";
import { compareDemographics } from "./compareDemographics";
import { ShowInactiveToggle } from "./ShowInactiveToggle";
import { calcAge, formatDob, formatGender } from "../formatMember";

export interface PatientInContextCardProps {
  ehrContext: EhrPatientContext;
  state: ResolutionState;
  /**
   * Receives the full match so the caller can route it correctly on navigate:
   * connect-only fallback members (synthetic pp: id + astranaId, no Mongo doc)
   * go to the library's astranaId path (/members/astrana:<astranaId>); real
   * Mongo members route by their _id.
   */
  onOpenMember: (member: EhrMemberMatch) => void;
  onRetry?: () => void;
  showInactive?: boolean;
  onShowInactiveChange?: (next: boolean) => void;
}

function patientLabel(ctx: EhrPatientContext): string {
  const name = [ctx.firstName, ctx.lastName].filter(Boolean).join(" ").trim();
  const fallback = `${ehrSourceSystemLabel(ctx.sourceSystem)} patient`;
  return [name || fallback, ctx.dob].filter(Boolean).join(" · ");
}

function MemberRow({
  member,
  onOpenMember,
}: {
  member: EhrMemberMatch;
  onOpenMember: (member: EhrMemberMatch) => void;
}): React.JSX.Element {
  const age = calcAge(member.dob);
  const plan = member.healthplanName ?? member.healthplanCode;
  const isPp = member._id.startsWith("pp:");
  return (
    <button
      type="button"
      disabled={!member._id}
      onClick={() => member._id && onOpenMember(member)}
      className="w-full text-left bg-card text-card-foreground rounded-2xl border border-border px-5 py-4 hover:border-primary hover:shadow-md hover:shadow-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-all duration-150 flex items-center justify-between gap-4 group disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
            {member.name ?? "Member"}
          </span>
          {age !== undefined && (
            <span className="text-sm text-muted-foreground">· {age}y</span>
          )}
          <span className="text-sm text-muted-foreground">
            · {formatGender(member.gender)}
          </span>
          {member.ipaCode && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 ring-1 ring-inset ring-blue-200 dark:ring-blue-700/50">
              IPA: {member.ipaCode}
            </span>
          )}
          {plan && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 ring-1 ring-inset ring-violet-200 dark:ring-violet-700/50">
              {plan}
            </span>
          )}
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm m-0">
          <Meta label="DOB" value={formatDob(member.dob)} />
          <Meta label="MPI" value={member.memberPolicyId ?? "—"} mono />
          {!isPp && <Meta label="LOB" value={member.lineOfBusiness ?? "—"} />}
        </dl>
      </div>
      <svg
        className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function Meta({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
        {label}
      </dt>
      <dd
        className={
          "m-0 text-sm text-foreground truncate " +
          (mono ? "font-mono" : "font-medium")
        }
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function SingleMatchSection({
  ehrContext,
  member,
  onOpenMember,
}: {
  ehrContext: EhrPatientContext;
  member: EhrMemberMatch;
  onOpenMember: (member: EhrMemberMatch) => void;
}): React.JSX.Element {
  const [confirmed, setConfirmed] = useState(false);
  const cmp = compareDemographics(ehrContext, member);

  const fieldLabel = (fields: ("name" | "dob")[]) =>
    fields.map((f) => (f === "dob" ? "date of birth" : "name")).join(" and ");

  return (
    <div className="flex flex-col gap-3">
      {cmp.kind === "soft" && (
        <p className="text-sm text-muted-foreground m-0">
          {`${fieldLabel(cmp.fields).charAt(0).toUpperCase()}${fieldLabel(cmp.fields).slice(1)} differs only by spacing/punctuation — please confirm.`}
        </p>
      )}

      {cmp.kind === "mismatch" && (
        <div className="flex flex-col gap-2">
          <div className="rounded-xl border border-yellow-200 dark:border-yellow-700/50 bg-yellow-100 dark:bg-yellow-900/30 px-4 py-3 text-sm text-yellow-700 dark:text-yellow-300 flex items-start gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 ring-yellow-200 dark:ring-yellow-700/50 flex-shrink-0">
              Mismatch
            </span>
            <span>{`This member's ${fieldLabel(cmp.fields)} does not match the ${ehrSourceSystemLabel(ehrContext.sourceSystem)} patient.`}</span>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-border"
            />
            I have verified this is the correct member
          </label>
        </div>
      )}

      {member._id && (cmp.kind !== "mismatch" || confirmed) && (
        <MemberRow member={member} onOpenMember={onOpenMember} />
      )}

      {member._id && cmp.kind === "mismatch" && !confirmed && (
        <button
          type="button"
          disabled
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Open member
        </button>
      )}
    </div>
  );
}

export function PatientInContextCard({
  ehrContext,
  state,
  onOpenMember,
  onRetry,
  showInactive = false,
  onShowInactiveChange,
}: PatientInContextCardProps): React.JSX.Element | null {
  if (state.kind === "idle") return null;

  return (
    <div
      className="bg-card text-card-foreground rounded-2xl border border-border shadow-sm p-5 sm:p-6 flex flex-col gap-4"
      data-testid="patient-in-context"
    >
      {/* Header row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset bg-primary/10 text-primary ring-primary/20">
          In {ehrSourceSystemLabel(ehrContext.sourceSystem)}
        </span>
        <span className="text-sm font-semibold text-foreground">
          {patientLabel(ehrContext)}
        </span>
      </div>

      {state.kind === "resolving" && (
        <div className="flex flex-col gap-3">
          <div className="h-20 w-full rounded-2xl bg-muted animate-pulse" />
          <p className="text-sm text-muted-foreground m-0">Matching to an Astrana member…</p>
        </div>
      )}

      {state.kind === "error" && (
        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {state.message}
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md self-start"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {state.kind === "none" && (
        <p className="text-sm text-muted-foreground m-0">
          No exact EHR match — showing closest matches below.
        </p>
      )}

      {(state.kind === "single" || state.kind === "multiple") && (
        <>
          {onShowInactiveChange && (
            <div className="flex justify-end">
              <ShowInactiveToggle checked={showInactive} onChange={onShowInactiveChange} />
            </div>
          )}

          {state.kind === "single" && (
            <SingleMatchSection
              key={state.member._id}
              ehrContext={ehrContext}
              member={state.member}
              onOpenMember={onOpenMember}
            />
          )}

          {state.kind === "multiple" && (
            <div className="flex flex-col gap-2">
              {state.members.filter((member) => !!member._id).map((member) => (
                <MemberRow key={member._id} member={member} onOpenMember={onOpenMember} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
